import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, ThinkingDots, Typography} from 'views/components';

import ChatMessage from './ChatMessage';
import {formatTokens} from './chatUtils';

// Distance from the bottom, in px, within which the list keeps following new output.
const FOLLOW_THRESHOLD_PX = 80;

const ChatMessageList = ({messages, loading, streaming, liveUsage, stopping, emptyText, followKey}) => {
  const bottomRef = React.useRef(null);
  const [scrollRoot, setScrollRoot] = React.useState(null);
  // Follow new output only while the reader is at the bottom of the chat.
  const stickToBottomRef = React.useRef(true);

  // The scene changes followKey on send, on a session switch and on a new chat, to go back to following.
  React.useEffect(() => {
    stickToBottomRef.current = true;
  }, [followKey]);

  React.useEffect(() => {
    if (!stickToBottomRef.current) return;
    // A smooth scroll per streamed render would restart its animation several times a second.
    bottomRef.current?.scrollIntoView({behavior: loading ? 'auto' : 'smooth'});
  }, [messages, loading, followKey]);

  const handleScroll = React.useCallback((e) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_THRESHOLD_PX;
  }, []);

  return (
    <Box ref={setScrollRoot} onScroll={handleScroll} sx={{flex: 1, overflowY: 'auto', px: '40px', py: '32px'}}>
      {messages.length === 0 && !loading && (
        <Box sx={{display: 'flex', justifyContent: 'center', pt: '60px'}}>
          <Typography sx={{fontSize: '14px', color: '#A8A49E'}}>
            {emptyText}
          </Typography>
        </Box>
      )}
      <Stack sx={{gap: '16px', maxWidth: '720px', mx: 'auto'}}>
        {messages.map((msg, i) => (
          <ChatMessage key={i} msg={msg} scrollRoot={scrollRoot} />
        ))}

        {loading && !streaming && (
          <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
            <Box sx={{px: '14px', py: '12px', borderRadius: '14px 14px 14px 4px', backgroundColor: '#F8F6F3', border: '1px solid #E8E4DE'}}>
              <ThinkingDots />
            </Box>
          </Box>
        )}
        {loading && (liveUsage || stopping) && (
          <Stack direction='row' alignItems='center' sx={{gap: '6px', px: '4px', mt: '-10px'}}>
            {liveUsage && (
              <Typography sx={{fontSize: '10px', color: '#A8A49E', lineHeight: '100%'}}>
                {formatTokens(liveUsage)}
              </Typography>
            )}
            {stopping && (
              <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%'}}>
                Stopping...
              </Typography>
            )}
          </Stack>
        )}

        <div ref={bottomRef} />
      </Stack>
    </Box>
  );
};

ChatMessageList.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  streaming: PropTypes.bool.isRequired,
  liveUsage: PropTypes.object,
  stopping: PropTypes.bool.isRequired,
  emptyText: PropTypes.string.isRequired,
  followKey: PropTypes.number.isRequired,
};

export default ChatMessageList;
